import React, { useEffect, useId, useState } from "react";
import {
  Box,
  Button,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";

import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";

import { useFonte } from "../contextos/FonteContext";
import { tamanhosControlesGlobais } from "../estilos/global";

const niveisFonte = [
  { percentual: 80, nome: "Menor" },
  { percentual: 90, nome: "Pequena" },
  { percentual: 100, nome: "Padrão" },
  { percentual: 110, nome: "Média" },
  { percentual: 120, nome: "Grande" },
  { percentual: 130, nome: "Maior" },
];

export default function ControleFonte() {
  const {
    percentual,
    habilitado,
    aumentarFonte,
    diminuirFonte,
    restaurarFonte,
  } = useFonte();

  const [anchorEl, setAnchorEl] = useState(null);
  const id = useId();
  const idBotao = `controle-fonte-botao-${id}`;
  const idMenu = `controle-fonte-menu-${id}`;
  const aberto = Boolean(anchorEl);

  useEffect(() => {
    if (!habilitado) {
      setAnchorEl(null);
    }
  }, [habilitado]);

  const fecharMenu = () => {
    setAnchorEl(null);
  };

  const selecionarNivel = (novoPercentual) => {
    if (novoPercentual === 100) {
      restaurarFonte();
    } else {
      const quantidadePassos = Math.abs(novoPercentual - percentual) / 10;
      const ajustar =
        novoPercentual > percentual ? aumentarFonte : diminuirFonte;

      for (let passo = 0; passo < quantidadePassos; passo += 1) {
        ajustar();
      }
    }

    fecharMenu();
  };

  const restaurarPadrao = () => {
    restaurarFonte();
    fecharMenu();
  };

  if (!habilitado) {
    return null;
  }

  return (
    <>
      <Button
        id={idBotao}
        type="button"
        variant="outlined"
        color="inherit"
        aria-label={`Tamanho da fonte: ${percentual}%. Abrir opções`}
        aria-controls={aberto ? idMenu : undefined}
        aria-haspopup="menu"
        aria-expanded={aberto}
        title={`Tamanho da fonte: ${percentual}%`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={
          <ArrowDropDownRoundedIcon
            aria-hidden="true"
            sx={{ fontSize: "18px !important" }}
          />
        }
        sx={{
          minWidth: 60,
          minHeight: 44,
          px: 1.25,
          borderRadius: 1,
          flexShrink: 0,
          fontSize: tamanhosControlesGlobais.botao,
          fontWeight: 500,
          textTransform: "none",
          whiteSpace: "nowrap",
          "& .MuiButton-endIcon": {
            ml: 0.5,
          },
        }}
      >
        Aa
      </Button>

      <Menu
        id={idMenu}
        anchorEl={anchorEl}
        open={aberto}
        onClose={fecharMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{
          "aria-labelledby": idBotao,
          sx: { p: 1 },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              width: "max-content",
              minWidth: 224,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
      >
        <ListSubheader
          component="div"
          disableSticky
          sx={{
            px: 1.25,
            py: 0.75,
            bgcolor: "transparent",
            color: "text.primary",
            fontSize: tamanhosControlesGlobais.botao,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          Tamanho da fonte
        </ListSubheader>

        {niveisFonte.map((nivel) => {
          const selecionado = percentual === nivel.percentual;

          return (
            <MenuItem
              key={nivel.percentual}
              role="menuitemradio"
              aria-checked={selecionado}
              selected={selecionado}
              onClick={() => selecionarNivel(nivel.percentual)}
              sx={{
                minHeight: 44,
                px: 1.25,
                gap: 1,
                borderRadius: 1,
                fontSize: tamanhosControlesGlobais.botao,
                "&.Mui-selected": {
                  color: "primary.main",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#1c4265" : "#e8f4ff",
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: 44,
                  px: 0.5,
                  py: 0.5,
                  borderRadius: 0.75,
                  bgcolor: "action.hover",
                  textAlign: "center",
                  fontSize: tamanhosControlesGlobais.rotulo,
                }}
              >
                {nivel.percentual}%
              </Box>

              <Box component="span" sx={{ flexGrow: 1 }}>
                {nivel.nome}
              </Box>

              {selecionado && (
                <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
              )}
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.75 }} />

        <MenuItem
          onClick={restaurarPadrao}
          sx={{
            minHeight: 44,
            px: 1.25,
            gap: 1,
            borderRadius: 1,
            color: "primary.main",
            fontSize: tamanhosControlesGlobais.botao,
            fontWeight: 500,
            whiteSpace: "normal",
          }}
        >
          <RestartAltRoundedIcon aria-hidden="true" sx={{ fontSize: 18, flexShrink: 0 }} />
          Restaurar padrão (100%)
        </MenuItem>
      </Menu>
    </>
  );
}
